import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBars,
  faChevronDown,
  faCheck,
  faPlus,
  faPencil,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FormsModule } from '@angular/forms';

import { ShoppingList } from '../../models/shopping-list';
import { ShoppingService } from '../../services/shopping/shopping.service';
import { UiModalComponent, UiToastService } from '../../shared/ui';

@Component({
  selector: 'app-shopping-list-switcher',
  standalone: true,
  imports: [FontAwesomeModule, FormsModule, UiModalComponent],
  templateUrl: './shopping-list-switcher.component.html',
  styleUrl: './shopping-list-switcher.component.scss',
})
export class ShoppingListSwitcherComponent {
  private readonly shoppingService = inject(ShoppingService);
  private readonly toast = inject(UiToastService);

  @Input() lists: ShoppingList[] = [];
  @Input() activeListId: number | undefined;
  @Input() householdId: number | undefined;

  /** Emitted when a different list should become active */
  @Output() listSelected = new EventEmitter<ShoppingList>();
  /** Emitted after a new list is created */
  @Output() listCreated = new EventEmitter<ShoppingList>();
  /** Emitted after a list is renamed */
  @Output() listRenamed = new EventEmitter<ShoppingList>();
  /** Emitted after a list is deleted */
  @Output() listDeleted = new EventEmitter<number>();

  // ── Icons ─────────────────────────────────────────────────────────────────
  faChevronDown = faChevronDown;
  faCheck = faCheck;
  faPlus = faPlus;
  faPencil = faPencil;
  faTrash = faTrash;
  faBars = faBars;

  // ── Dropdown state ────────────────────────────────────────────────────────
  dropdownOpen = false;
  overflowMenuOpen = false;

  // ── Create flow ───────────────────────────────────────────────────────────
  @ViewChild('createModal') createModal!: UiModalComponent;
  newListName = '';
  creating = false;

  // ── Rename flow ───────────────────────────────────────────────────────────
  @ViewChild('renameModal') renameModal!: UiModalComponent;
  renamingList: ShoppingList | null = null;
  renameValue = '';
  renaming = false;

  // ── Delete flow ───────────────────────────────────────────────────────────
  @ViewChild('deleteModal') deleteModal!: UiModalComponent;
  deletingList: ShoppingList | null = null;
  deleting = false;

  get activeList(): ShoppingList | undefined {
    return this.lists.find(l => l.id === this.activeListId);
  }

  // ── Dropdown ──────────────────────────────────────────────────────────────

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeDropdown();
    this.overflowMenuOpen = false;
  }

  toggleOverflowMenu(event: Event): void {
    event.stopPropagation();
    this.overflowMenuOpen = !this.overflowMenuOpen;
  }

  openRenameActive(event: Event): void {
    this.overflowMenuOpen = false;
    const list = this.activeList;
    if (list) this.openRename(list, event);
  }

  openDeleteActive(event: Event): void {
    this.overflowMenuOpen = false;
    const list = this.activeList;
    if (list) this.openDelete(list, event);
  }

  selectList(list: ShoppingList): void {
    if (list.id === this.activeListId) { this.closeDropdown(); return; }
    this.closeDropdown();
    this.listSelected.emit(list);
  }

  // ── Context menu per list ─────────────────────────────────────────────────
  // (removed — actions moved to burger menu)

  // ── Create ────────────────────────────────────────────────────────────────

  openCreate(): void {
    this.newListName = 'New list';
    this.closeDropdown();
    this.createModal.open();
    // auto-select text so user can immediately type a name
    setTimeout(() => {
      const input = document.getElementById('new-list-name-input') as HTMLInputElement | null;
      input?.select();
    }, 80);
  }

  submitCreate(): void {
    const name = this.newListName.trim();
    if (!name || !this.householdId || this.creating) return;
    this.creating = true;
    this.shoppingService.createList(this.householdId, { name }).subscribe({
      next: list => {
        this.creating = false;
        this.createModal.close();
        this.listCreated.emit(list);
      },
      error: () => {
        this.creating = false;
        this.toast.error('Could not create list.');
      },
    });
  }

  // ── Rename ────────────────────────────────────────────────────────────────

  openRename(list: ShoppingList, event: Event): void {
    event.stopPropagation();
    this.closeDropdown();
    this.renamingList = list;
    this.renameValue = list.name;
    this.renameModal.open();
    setTimeout(() => {
      const input = document.getElementById('rename-list-input') as HTMLInputElement | null;
      input?.select();
    }, 80);
  }

  submitRename(): void {
    const name = this.renameValue.trim();
    if (!name || !this.householdId || !this.renamingList || this.renaming) return;
    this.renaming = true;
    this.shoppingService.updateList(this.renamingList.id, { name }).subscribe({
      next: updated => {
        this.renaming = false;
        this.renameModal.close();
        this.listRenamed.emit(updated);
      },
      error: () => {
        this.renaming = false;
        this.toast.error('Could not rename list.');
      },
    });
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  openDelete(list: ShoppingList, event: Event): void {
    event.stopPropagation();
    this.closeDropdown();
    this.deletingList = list;
    this.deleteModal.open();
  }

  submitDelete(): void {
    if (!this.householdId || !this.deletingList || this.deleting) return;
    const listId = this.deletingList.id;
    this.deleting = true;
    this.shoppingService.deleteList(listId).subscribe({
      next: () => {
        this.deleting = false;
        this.deleteModal.close();
        this.listDeleted.emit(listId);
      },
      error: () => {
        this.deleting = false;
        this.toast.error('Could not delete list.');
      },
    });
  }

  canDelete(): boolean {
    return this.lists.length > 1;
  }
}


















